import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {FiArrowLeft, FiArrowRight, FiCheck, FiEye, FiEyeOff, FiLock,FiMail, FiShield} from "react-icons/fi";
import baseURL from "../../config/api";

type ChangePasswordValues = {
    encryptedId: string;
    newPassword: string;
    confirmPassword: string;
};

type FamilyMemberResponse = {
    firstName?: string;
    lastName?: string;
    email?: string;
    FirstName?: string;
    LastName?: string;
    Email?: string;
};

const GET_FAMILY_MEMBER_URL = `${baseURL}FamilyMember/GetFamilyMemberByEncryptedId`;

const UPDATE_PASSWORD_URL = `${baseURL}FamilyMember/updatePasswordByEncryptedId`;

const ChangePassword: React.FC = () => {
    const [searchParams] = useSearchParams();

    const encryptedId = searchParams.get("member") ??"";

    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [loadingMember, setLoadingMember] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [success, setSuccess] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const loadFamilyMember = async () => {
            setEmail("");
            setErrorMessage("");
            setSuccess(false);

            if (!encryptedId) {
                setErrorMessage("The password setup link is missing its encrypted ID. Please use the link from your email.");
                setLoadingMember(false);
                return;
            }

            try {
                setLoadingMember(true);

                const response = await axios.get<FamilyMemberResponse>(
                    GET_FAMILY_MEMBER_URL,
                    {
                        params: { encryptedId },
                    }
                );

                if (!cancelled) {
                    const member = response.data;

                    setEmail(
                        member.email ??
                        member.Email ??
                        ""
                    );
                }
            } catch (error: unknown) {
                if (!cancelled) {
                    setErrorMessage(getApiErrorMessage(
                        error,
                        "Unable to validate the password setup link. Please request a new link."
                    ));
                }
            } finally {
                if (!cancelled) {
                    setLoadingMember(false);
                }
            }
        };

        void loadFamilyMember();

        return () => {
            cancelled = true;
        };
    }, [encryptedId]);

    // Password requirements.
    const requirements = [
        {
            label: "At least 8 characters",
            valid: newPassword.length >= 8
        },
        {
            label: "One uppercase letter (A-Z)",
            valid: /[A-Z]/.test(newPassword)
        },
        {
            label: "One lowercase letter (a-z)",
            valid: /[a-z]/.test(newPassword)
        },
        {
            label: "One number (0-9)",
            valid: /\d/.test(newPassword)
        },
        {
            label: "One special character (!@#$%^&*())",
            valid: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
        }
    ];

    const isPasswordValid = requirements.every((requirement) => requirement.valid);

    const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

    const strength = requirements.filter((requirement) => requirement.valid).length;

    const strengthLabel =
        strength <= 2
            ? "Weak"
            : strength <= 4
                ? "Medium"
                : "Strong";

    const strengthColor =
        strength <= 2
            ? "bg-red-500"
            : strength <= 4
                ? "bg-amber-500"
                : "bg-emerald-500";

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword(event.target.value);
        setPasswordTouched(true);
        setErrorMessage("");
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setErrorMessage("");
        setPasswordTouched(true);

        if (!encryptedId) {
            setErrorMessage("Invalid password setup link.");
            return;
        }

        if (loadingMember) {
            setErrorMessage("Please wait while we validate your account.");
            return;
        }

        if (!email) {
            setErrorMessage("We could not validate your account. Please request a new password setup link.");
            return;
        }

        if (!isPasswordValid) {
            setErrorMessage("Please meet all password requirements before continuing.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMessage("New password and confirm password do not match.");
            return;
        }

        try {
            setLoading(true);

            const request: ChangePasswordValues = {
                encryptedId,
                newPassword,
                confirmPassword
            };

            await axios.post(
                UPDATE_PASSWORD_URL,
                request
            );

            setSuccess(true);
            setNewPassword("");
            setConfirmPassword("");
            setPasswordTouched(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        } catch (error: unknown) {
            setErrorMessage(
                getApiErrorMessage(
                    error,
                    "Unable to change your password. Please try again."
                )
            );
        } finally {
            setLoading(false);
        }
    }

    const inputClass = "w-full text-sm border border-gray-300 px-4 py-3 rounded-md outline-none focus:border-custom-blue focus:ring-1 focus:ring-custom-blue disabled:bg-gray-100";

    return (
        <div className="font-[sans-serif] text-custom-blue">
            <div className="min-h-screen flex flex-col items-center justify-center py-8 px-4">
                <div className="grid md:grid-cols-2 items-center gap-8 max-w-7xl w-full">
                    <div className="border border-gray-300 rounded-md p-6 sm:p-8 w-full max-w-md shadow-[0_2px_22px_-4px_rgba(93,96,127,0.2)] max-md:mx-auto">

                        <div className="mb-8">
                            <Link
                                to="/sign-in"
                                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-secondary transition"
                            >
                                <FiArrowLeft size={16} />
                                Back to sign in
                            </Link>

                            <div className="flex items-center gap-x-[0.875rem]">
                                <div className="mt-7 mb-5 flex h-12 w-12 items-center justify-center rounded-md bg-gray-100 text-custom-blue">
                                    <FiLock size={24} />
                                </div>

                                <h1 className="text-3xl font-extrabold">
                                    Change password
                                </h1>
                            </div>

                            <p className="mt-3 text-sm leading-6 text-gray-500">
                                Create a strong new password to keep your My
                                Church Directory account secure.
                            </p>
                        </div>

                        {
                            success ? (
                                <div
                                    role="status"
                                    className="rounded-md border border-green-200 bg-green-50 p-5"
                                >
                                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-green-100 text-green-700">
                                        <FiCheck size={24} />
                                    </div>

                                    <h2 className="text-xl font-bold text-gray-800">
                                        Password changed!
                                    </h2>

                                    <p className="mt-2 text-sm leading-6 text-gray-600">
                                        Your password has been updated successfully.
                                        You can now sign in using your new password.
                                    </p>

                                    <Link
                                        to="/sign-in"
                                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-custom-blue hover:bg-blue-900 px-4 py-3 text-sm font-semibold text-white transition"
                                    >
                                        Continue to sign in
                                        <FiArrowRight size={17} />
                                    </Link>
                                </div>
                            ) : (
                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-5"
                                >
                                    <div>
                                        <label
                                            className="text-sm mb-2 block font-medium"
                                            htmlFor="email"
                                        >
                                            Email / Username
                                        </label>

                                        <div className="flex items-center gap-3 rounded-md bg-gray-50 px-4 py-3">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-custom-blue/10">
                                                <FiMail
                                                    size={18}
                                                    className="text-custom-blue"
                                                />
                                            </div>

                                            <span
                                                id="email"
                                                className="min-w-0 flex-1 break-all text-sm text-gray-600"
                                            >
                                                {loadingMember ? "Loading account..." : email || "Email unavailable"}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="newPassword"
                                            className="text-sm mb-2 block font-medium"
                                        >
                                            New password
                                        </label>

                                        <div className="relative flex items-center">
                                            <input
                                                id="newPassword"
                                                name="newPassword"
                                                type={showNewPassword ? "text" : "password"}
                                                autoComplete="new-password"
                                                required
                                                minLength={8}
                                                disabled={loading || loadingMember}
                                                value={newPassword}
                                                onChange={handlePasswordChange}
                                                aria-invalid={passwordTouched && !isPasswordValid}
                                                aria-describedby="password-requirements"
                                                className={`${inputClass} pr-11`}
                                                placeholder="Enter new password"
                                            />

                                            <button
                                                type="button"
                                                disabled={loading}
                                                onClick={() =>
                                                    setShowNewPassword((previous) => !previous)
                                                }
                                                aria-label={showNewPassword ? "Hide password" : "Show password"}
                                                className="absolute right-4 text-gray-400 hover:text-custom-blue"
                                            >
                                                {
                                                    showNewPassword ? (
                                                        <FiEyeOff size={18} />
                                                    ) : (
                                                        <FiEye size={18} />
                                                    )
                                                }
                                            </button>
                                        </div>

                                        {
                                            newPassword.length > 0 && (
                                                <div
                                                    id="password-requirements"
                                                    className="mt-3"
                                                >
                                                    <div className="mb-2 flex items-center justify-between">
                                                        <span className="text-xs text-gray-500">
                                                            Password strength
                                                        </span>

                                                        <span
                                                            className={`text-xs font-semibold ${strength <= 2
                                                                    ? "text-red-500"
                                                                    : strength <= 4
                                                                        ? "text-amber-600"
                                                                        : "text-green-600"
                                                                }`}
                                                        >
                                                            {strengthLabel}
                                                        </span>
                                                    </div>

                                                    <div
                                                        className="flex gap-1"
                                                        aria-label={`Password strength: ${strength} of 5 requirements met`}
                                                    >
                                                        {
                                                            requirements.map(
                                                                (item, index) => (
                                                                    <div
                                                                        key={item.label}
                                                                        className={`h-1.5 flex-1 rounded-full ${index < strength
                                                                                ? strengthColor
                                                                                : "bg-gray-200"
                                                                            }`}
                                                                    />
                                                                )
                                                            )
                                                        }
                                                    </div>

                                                    <ul className="mt-3 space-y-2">
                                                        {
                                                            requirements.map((item) => (
                                                                <li
                                                                    key={item.label}
                                                                    className={`flex items-center gap-2 text-xs ${item.valid
                                                                            ? "text-green-700"
                                                                            : "text-gray-500"
                                                                        }`}
                                                                >
                                                                    <FiCheck
                                                                        size={14}
                                                                        className={
                                                                            item.valid
                                                                                ? "text-green-600"
                                                                                : "text-gray-300"
                                                                        }
                                                                    />
                                                                    {item.label}
                                                                </li>
                                                            ))
                                                        }
                                                    </ul>
                                                </div>
                                            )
                                        }

                                        {
                                            passwordTouched &&
                                            newPassword.length > 0 &&
                                            !isPasswordValid && (
                                                <p className="mt-2 text-xs text-red-500">
                                                    Your password does not meet all
                                                    the required criteria above.
                                                </p>
                                            )
                                        }
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="confirmPassword"
                                            className="text-sm mb-2 block font-medium"
                                        >
                                            Confirm password
                                        </label>

                                        <div className="relative flex items-center">
                                            <input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type={showConfirmPassword ? "text" : "password"}
                                                autoComplete="new-password"
                                                required
                                                disabled={loading || loadingMember}
                                                value={confirmPassword}
                                                onChange={(event) => {
                                                    setConfirmPassword(event.target.value);
                                                    setErrorMessage("");
                                                }}
                                                aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                                                className={`${inputClass} pr-11`}
                                                placeholder="Confirm new password"
                                            />

                                            <button
                                                type="button"
                                                disabled={loading}
                                                onClick={() =>
                                                    setShowConfirmPassword((previous) => !previous)
                                                }
                                                aria-label={
                                                    showConfirmPassword
                                                        ? "Hide confirm password"
                                                        : "Show confirm password"
                                                }
                                                className="absolute right-4 text-gray-400 hover:text-custom-blue"
                                            >
                                                {
                                                    showConfirmPassword ? (
                                                        <FiEyeOff size={18} />
                                                    ) : (
                                                        <FiEye size={18} />
                                                    )
                                                }
                                            </button>
                                        </div>

                                        {
                                            confirmPassword.length > 0 && (
                                                <p
                                                    className={`mt-2 flex items-center gap-1 text-xs ${passwordsMatch
                                                            ? "text-green-600"
                                                            : "text-red-500"
                                                        }`}
                                                >
                                                    {
                                                        passwordsMatch ? (
                                                            <>
                                                                <FiCheck size={14} />
                                                                Passwords match
                                                            </>
                                                        ) : (
                                                            "Passwords do not match"
                                                        )
                                                    }
                                                </p>
                                            )
                                        }
                                    </div>


                                    {errorMessage && (
                                        <div
                                            role="alert"
                                            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600"
                                        >
                                            {errorMessage}
                                        </div>
                                    )
                                    }

                                    <div className="!mt-8">
                                        <button
                                            type="submit"
                                            disabled={
                                                loading ||
                                                loadingMember ||
                                                !encryptedId ||
                                                !email
                                            }
                                            className="w-full shadow-xl py-3 px-4 text-sm font-semibold rounded text-white bg-custom-blue hover:bg-blue-900 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 transition"
                                        >
                                            {
                                                loading
                                                    ? (
                                                        "Updating password..."
                                                    ) : loadingMember ? (
                                                        "Loading account..."
                                                    ) : (
                                                        <span className="inline-flex items-center justify-center gap-2">
                                                            Update password
                                                            <FiArrowRight size={17} />
                                                        </span>
                                                    )
                                            }
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                                        <FiShield
                                            size={16}
                                            className="text-green-600"
                                        />
                                        Your account security is our priority.
                                    </div>
                                </form>
                            )
                        }

                        <div className="mt-8 border-t border-gray-200 pt-5">
                            <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                                <Link
                                    to="/privacy-policy"
                                    className="hover:text-secondary"
                                >
                                    Privacy Policy
                                </Link>

                                <Link
                                    to="/terms-of-service"
                                    className="hover:text-secondary"
                                >
                                    Terms of Service
                                </Link>

                                <a
                                    href="/help"
                                    className="hover:text-secondary"
                                >
                                    Help &amp; Support
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="max-w-xl w-full mx-auto">
                        <div className="overflow-hidden rounded-md">
                            <img
                                src="/images/sign-in-image-png.png"
                                className="w-full max-h-[420px] object-cover"
                                alt="My Church Directory"
                            />
                        </div>

                        <div className="mt-6 text-center">
                            <h2 className="text-xl font-bold">
                                My Church Directory
                            </h2>

                            <p className="mt-2 text-sm text-gray-500">
                                Stay connected with your church community
                                wherever you go.
                            </p>
                        </div>

                        <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
                            <a
                                href="https://apps.apple.com/us/app/my-church-directory-app/id6787551672"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Download My Church Directory on the App Store"
                                className="inline-flex items-center justify-center transition-transform duration-300 hover:scale-105"
                            >
                                <img
                                    src="/images/apple-store-badge.png"
                                    alt="Download on the App Store"
                                    className="block h-12 sm:h-14 w-auto object-contain"
                                />
                            </a>

                            <a
                                href="https://play.google.com/store/apps/details?id=com.datarepo.directory&hl=en_US"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Download My Church Directory on Google Play"
                                className="inline-flex items-center justify-center transition-transform duration-300 hover:scale-105"
                            >
                                <img
                                    src="/images/google-play-badge.png"
                                    alt="Get it on Google Play"
                                    className="block h-12 sm:h-14 w-auto object-contain"
                                />
                            </a>
                        </div>

                        <p className="mt-5 text-center text-xs text-gray-400">
                            © {new Date().getFullYear()} My Church Directory.
                            All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

function getApiErrorMessage(
    error: unknown,
    fallback: string
): string {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data;

        if (typeof data === "string" && data.trim()) {
            return data;
        }

        if (data && typeof data === "object") {
            const body = data as {
                message?: string;
                title?: string;
                errors?: Record<string, string[]>;
            };

            if (body.message) {
                return body.message;
            }

            if (body.title) {
                return body.title;
            }

            if (body.errors) {
                const validationMessage = Object.values(body.errors)
                    .flat()
                    .find((message) => Boolean(message));

                if (validationMessage) {
                    return validationMessage;
                }
            }
        }

        if (error.message && !error.response) {
            return "Unable to reach the server. Please check your connection and try again.";
        }
    }

    return fallback;
}

export default ChangePassword;
